import React, { useState, useEffect } from 'react';
import { Shield, FileText, RefreshCcw, Save, Eye, Edit2, Check } from 'lucide-react';
import api from '../../api/api';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

const PolicyManager = () => {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('privacy');
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [editTitle, setEditTitle] = useState('');

  const policyTypes = [
    { id: 'privacy', name: 'Privacy Policy', icon: Shield, color: 'blue' },
    { id: 'terms', name: 'Terms of Use', icon: FileText, color: 'green' },
    { id: 'refund', name: 'Refund Policy', icon: RefreshCcw, color: 'purple' }
  ];

  useEffect(() => {
    fetchPolicies();
  }, []);

  const fetchPolicies = async () => {
    try {
      setLoading(true);
      const response = await api.get('/policies/tenant');
      setPolicies(response.data.policies || []);
    } catch (err) {
      toast.error('Failed to load policies');
    } finally {
      setLoading(false);
    }
  };

  const getCurrentPolicy = () => {
    return policies.find(p => p.policy_type === activeTab) || { content: '', title: '' };
  };

  const handleEdit = () => {
    const current = getCurrentPolicy();
    setEditContent(current.content || '');
    setEditTitle(current.title || policyTypes.find(p => p.id === activeTab)?.name || '');
    setEditMode(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put(`/policies/tenant/${activeTab}`, {
        title: editTitle,
        content: editContent,
        is_active: true
      });
      toast.success('Policy saved successfully');
      setEditMode(false);
      fetchPolicies();
    } catch (err) {
      toast.error('Failed to save policy');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    setEditContent('');
    setEditTitle('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const currentPolicy = getCurrentPolicy();
  const ActiveIcon = policyTypes.find(p => p.id === activeTab)?.icon || FileText;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Policy Manager</h1>
        <p className="text-gray-600 mt-1">
          Manage your institution's legal policies for payment gateway compliance
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b pb-4">
        {policyTypes.map((policy) => {
          const Icon = policy.icon;
          const isActive = activeTab === policy.id;
          const hasContent = policies.find(p => p.policy_type === policy.id)?.content;
          
          return (
            <button
              key={policy.id}
              onClick={() => {
                setActiveTab(policy.id);
                setEditMode(false);
              }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                isActive
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span>{policy.name}</span>
              {hasContent && (
                <Check className="w-4 h-4 text-green-400" />
              )}
            </button>
          );
        })}
      </div>

      {/* Content Area */}
      <div className="bg-white rounded-xl shadow-sm border">
        {/* Toolbar */}
        <div className="flex items-center justify-between p-4 border-b bg-gray-50">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-blue-100 text-blue-600`}>
              <ActiveIcon className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">
                {editMode ? editTitle : (currentPolicy.title || policyTypes.find(p => p.id === activeTab)?.name)}
              </h2>
              <p className="text-sm text-gray-500">
                {currentPolicy.content ? 'Custom policy configured' : 'Using default template'}
              </p>
            </div>
          </div>
          
          <div className="flex gap-2">
            {editMode ? (
              <>
                <button
                  onClick={handleCancel}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  {saving ? 'Saving...' : 'Save Policy'}
                </button>
              </>
            ) : (
              <>
                <a
                  href={`/${window.location.pathname.split('/')[1]}/policy/${activeTab}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  <Eye className="w-4 h-4" />
                  Preview
                </a>
                <button
                  onClick={handleEdit}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Edit2 className="w-4 h-4" />
                  Edit Policy
                </button>
              </>
            )}
          </div>
        </div>

        {/* Editor / Preview */}
        <div className="p-6">
          {editMode ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Policy Title
                </label>
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter policy title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Policy Content (Markdown supported)
                </label>
                <textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  rows={20}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                  placeholder="Enter your policy content here. You can use Markdown formatting..."
                />
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-800 mb-2">Markdown Tips</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li><code className="bg-blue-100 px-1 rounded"># Heading 1</code> - Main heading</li>
                  <li><code className="bg-blue-100 px-1 rounded">## Heading 2</code> - Sub heading</li>
                  <li><code className="bg-blue-100 px-1 rounded">**bold**</code> - Bold text</li>
                  <li><code className="bg-blue-100 px-1 rounded">- item</code> - Bullet list</li>
                  <li><code className="bg-blue-100 px-1 rounded">1. item</code> - Numbered list</li>
                </ul>
              </div>
            </div>
          ) : (
            <div className="prose prose-blue max-w-none">
              {currentPolicy.content ? (
                <ReactMarkdown>{currentPolicy.content}</ReactMarkdown>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p className="text-lg">No custom policy configured</p>
                  <p className="text-sm mt-2">Click &quot;Edit Policy&quot; to create your custom policy, or a default template will be shown to users.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Info Card */}
      <div className="mt-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
        <h3 className="font-semibold text-blue-900 mb-2">Payment Gateway Compliance</h3>
        <p className="text-blue-700 text-sm">
          These policies are required for payment gateway integration (Razorpay, PayU, etc.). 
          Make sure to configure all three policies: Privacy Policy, Terms of Use, and Refund Policy.
        </p>
      </div>
    </div>
  );
};

export default PolicyManager;
